import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';

import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import { LOAD_MORE, REFETCH } from 'src/util/constants';

import AdminMeterTradeRulesItem from './AdminMeterTradeRulesItem';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactComponentElement} -AdminMeterTradeRulesTable component
 */
function AdminMeterTradeRulesTable(props) {
  if (!props) {
    return <Loading />;
  }

  const {
    meter, rules, moreFunc, hasMore, updating,
  } = props;
  const { tradePointId, property } = meter;
  const { uuid: propertyId } = property;

  const trades = (!rules || !rules.edges)
    ? [] : rules.edges.map((el) => (el.node));

  const intl = useIntl();
  const headerClauses = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.clauses.label', defaultMessage: 'Clauses' });

  // Table header translations

  const headerTradeRuleId = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.id.label', defaultMessage: 'ID' });
  const headerTradeRuleIdTitle = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.id.title', defaultMessage: 'identifier' });
  const headerTradeRuleColespanSeparator = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.colspan.separator.', defaultMessage: '/' });
  const headerTradeRuleDirection = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.direction.label', defaultMessage: 'Direction' });
  const headerTradeRuleType = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.type.label', defaultMessage: 'Type' });
  const headerTradeRuleState = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.state.label', defaultMessage: 'State' });

  const headerIdDirectionTypeState = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.id_direction_type_state.label', defaultMessage: '{headerTradeRuleId} {headerTradeRuleColespanSeparator} {headerTradeRuleDirection} {headerTradeRuleColespanSeparator} {headerTradeRuleType} {headerTradeRuleColespanSeparator} {headerTradeRuleState}' }, {
    headerTradeRuleId: <abbr title={headerTradeRuleIdTitle}>{headerTradeRuleId}</abbr>,
    headerTradeRuleColespanSeparator,
    headerTradeRuleDirection,
    headerTradeRuleType,
    headerTradeRuleState,
  });

  const headercounterpartyTrader = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_show.admin_meter_trade_rules_table.counterparty_trader_header',
    defaultMessage: 'Counterparty Trader',
  });
  const headercounterpartyTradePoint = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_show.admin_meter_trade_rules_table.counterparty_trade_point_header',
    defaultMessage: 'Counterparty Trade Point',
  });
  const headerValidity = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_show.admin_meter_trade_rules_table.validity_header',
    defaultMessage: 'Validity',
  });

  // Load more button translations
  const loadMoreStateLoadMore = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_show.admin_meter_trade_rules_table.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const loadMoreStateNoMore = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_show.admin_meter_trade_rules_table.load_more_button.state_no_more.label',
    defaultMessage: 'No more trade rules to load',
  });

  return (
    <div className="mt-4 mb-4 card">
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">
                {headerIdDirectionTypeState}
              </th>
              <th scope="col">{headercounterpartyTrader}</th>
              <th scope="col">{headercounterpartyTradePoint}</th>
              <th scope="col">{headerClauses}</th>
              <th scope="col">{headerValidity}</th>
            </tr>
          </thead>
          <tbody>
            {updating !== REFETCH && trades
              && trades.map((tradeRule) => (
                <AdminMeterTradeRulesItem
                  tradePointId={tradePointId}
                  rule={tradeRule}
                  propertyId={propertyId}
                  key={tradeRule.uuid}
                />
              ))}
            {(updating === LOAD_MORE || updating === REFETCH) && (
              <tr>
                <td colSpan={5}>
                  <Loading />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="card-footer">
        <LoadMoreButton
          copyLoadMore={loadMoreStateLoadMore}
          copyNoMore={loadMoreStateNoMore}
          onClick={moreFunc}
          hasNext={updating === !LOAD_MORE || hasMore}
        />
      </div>
    </div>
  );
}

AdminMeterTradeRulesTable.propTypes = {
  meter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  rules: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  moreFunc: PropTypes.func.isRequired,
  hasMore: PropTypes.bool.isRequired,
  updating: PropTypes.string,
};

AdminMeterTradeRulesTable.defaultProps = {
  meter: null,
  rules: null,
  updating: null,
};

export default createFragmentContainer(
  AdminMeterTradeRulesTable,
  {
    rules: graphql`
      fragment AdminMeterTradeRulesTable_rules on TradeRuleConnection {
        edges {
          cursor
          node {
            uuid
            ...AdminMeterTradeRulesItem_rule
          }
        }
      }
    `,
  },
);
